import MongoTable from '../table/mongotable';
import React, { useState, useEffect, useMemo } from 'react';
import { useAxios } from '../../contexts/axios';
import { useDashboardProfile } from '../../contexts/dashboardProfile';
import { BACKEND_URL } from '../../configs';
import OvertimeDescription from '../table/overtimeDescription';
import DD2 from '../DD2';
import PrimaryButton from '../button/PrimaryButton';
import DatePickerInput from '../DatePickerInput';
import FlexRow from '../layout/FlexRow';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import TabPanel from '../tabs/tabPanel';
import { useMongoClustersMetadata } from '../../hooks/costing/useMongo';
import {
    Checkbox,
    FormControlLabel,
    LinearProgress,
} from '@mui/material';
const a11yProps = ({ index }) => {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
};

/** `YYYY-MM-DD` in the user's local calendar — `toISOString()` uses UTC and can shift the day. */
function formatLocalDateYYYYMMDD(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return '';
    }
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

/** Sum each period column across rows (matches MongoTable total line for the graph). */
function computeUnitCostingPeriodTotals(rows) {
    if (!Array.isArray(rows) || rows.length === 0) {
        return null;
    }
    const head = Object.keys(rows[0]);
    if (head.length < 2) {
        return null;
    }
    const totals = [];
    for (let c = 1; c < head.length; c++) {
        const colKey = head[c];
        let sum = 0;
        for (let r = 0; r < rows.length; r++) {
            const v = Number(rows[r][colKey]);
            sum += Number.isFinite(v) ? v : 0;
        }
        totals.push(sum);
    }
    return totals;
}

/** Work email from dashboard profile; must be a real address (not the placeholder). */
function getProfileOwnerEmail(profile) {
    const raw = profile?.email?.trim() ?? '';
    if (!raw || raw === 'email' || !raw.includes('@')) {
        return null;
    }
    const at = raw.indexOf('@');
    if (at <= 0 || at === raw.length - 1) {
        return null;
    }
    return raw;
}

/** API `cost-analysis/overall-by-owner` uses this dimension key (not cluster/vertical). */
const OWNERSHIP_TABLE_GROUP_BY = 'service';
const OWNERSHIP_TABLE_GROUP_BY_LABEL = 'Service';

function parseDdMmYyyyKey(key) {
    const parts = String(key).split('-');
    if (parts.length !== 3) {
        return 0;
    }
    const d = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    const y = parseInt(parts[2], 10);
    if (!Number.isFinite(d) || !Number.isFinite(m) || !Number.isFinite(y)) {
        return 0;
    }
    return new Date(y, m - 1, d).getTime();
}

/**
 * MongoTable expects the dimension column first in each row, then period keys in order.
 * API rows use `service` plus DD-MM-YYYY keys with plain number costs (not [increment, cost] tuples yet — MongoTable builds those).
 */
function normalizeOverallByOwnerRows(rows) {
    if (!Array.isArray(rows) || rows.length === 0) {
        return rows;
    }
    const dim = OWNERSHIP_TABLE_GROUP_BY;
    const dateKeys = Object.keys(rows[0]).filter((k) => k !== dim);
    dateKeys.sort((a, b) => parseDdMmYyyyKey(a) - parseDdMmYyyyKey(b));
    return rows.map((row) => {
        const out = {};
        out[dim] = row[dim];
        for (let i = 0; i < dateKeys.length; i++) {
            const k = dateKeys[i];
            const v = row[k];
            out[k] = typeof v === 'number' ? v : Number(v) || 0;
        }
        return out;
    });
}

export default function OwnershipBasedCostingTable() {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [finalData, setFinalData] = useState([]);
    const [totalData, setTotalData] = useState(null);
    const [ok, setOk] = useState(false);
    const authAxios = useAxios();
    const { profile, loading: profileLoading } = useDashboardProfile();
    const { data: mongoMappingData } = useMongoClustersMetadata();
    const [value, setValue] = useState(0);
    /** GRAPH tab: stack bars by group-by dimension (same chart as total columns). */
    const [showGroupBreakdown, setShowGroupBreakdown] = useState(false);

    const tools = [
        { name: 'Mongo', id: 'mongo' },
        { name: 'Elastic', id: 'elastic' },
        { name: 'Redis', id: 'redis' },
        { name: 'Bigquery Analysis', id: 'bigquery' },
        { name: 'Bigquery Analysis (Metabase)', id: 'bigquery' },
    ];
    const periodicities = [
        { name: 'Daily', id: 'daily' },
        { name: 'Weekly', id: 'weekly' },
        { name: 'Monthly', id: 'monthly' },
    ];

    var endDateMaxLimit = new Date();
    endDateMaxLimit.setDate(endDateMaxLimit.getDate() - 2);

    const tool = tools[0];
    const [periodicity, setPeriodicity] = useState(null);
    const [endDate, setEndDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 2);
        return d;
    });
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 2);
        return d;
    });

    const isInputValid = () => {
        if (profileLoading) {
            alert('Please wait for your dashboard profile to finish loading.');
            return false;
        }
        if (!getProfileOwnerEmail(profile)) {
            alert(
                'Your work email from the dashboard profile must include @ (e.g. name@domain.com). Open the profile menu in the header or check access services.'
            );
            return false;
        }
        if (!!!periodicity) {
            alert('Please select a periodicity for data');
            return false;
        }
        if (startDate > endDate) {
            alert('Please enter valid start and end date');
            return false;
        }
        return true;
    };

   
    const getUnitCostingData = async () => {
        if (!isInputValid() || loading) {
            return;
        }

        setLoading(true);
        try {
            const owner = getProfileOwnerEmail(profile);
            // Use `params` (not `?...` inside `url`) so the path never becomes `.../overall-by-owner/?…`,
            // which triggers a 307 to http:// and (blocked:mixed-content) on HTTPS sites.
            await authAxios.get('/cost-analysis/overall-by-owner', {
                params: {
                    startDate: formatLocalDateYYYYMMDD(startDate),
                    endDate: formatLocalDateYYYYMMDD(endDate),
                    periodicity: periodicity.id,
                    owner,
                },
            })
                .then((response) => {
                    const body = response.data;
                    const list = Array.isArray(body)
                        ? body
                        : Array.isArray(body?.data)
                          ? body.data
                          : [];
                    setData(normalizeOverallByOwnerRows(list));
                });
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!data?.length) {
            return;
        }
        const first = data[0];
        if (
            first &&
            typeof first.question === 'string' &&
            first.question.includes('__#Question#__:')
        ) {
            const finalList = [];
            for (let i = 0; i < data.length; i++) {
                finalList.push({ ...data[i] });
                finalList[i].question = finalList[i].question.split(
                    '__#Question#__:'
                )[1];
            }
            setFinalData(finalList);
        } else {
            setFinalData(data);
        }
    }, [data]);
    useEffect(() => {
        setData(null);
        setFinalData([]);
        setValue(0);
        setShowGroupBreakdown(false);
    }, [startDate, endDate, periodicity, profile?.email]);

    const graphRows =
        Array.isArray(finalData) && finalData.length > 0 ? finalData : data;
    const graphTotalData = useMemo(() => {
        if (!graphRows?.length) {
            return null;
        }
        return computeUnitCostingPeriodTotals(graphRows);
    }, [graphRows]);

    const graphPeriodCount =
        graphRows?.length > 0 && graphRows[0]
            ? Math.max(0, Object.keys(graphRows[0]).length - 1)
            : 0;
    const graphChartReady =
        graphPeriodCount > 0 &&
        Array.isArray(graphTotalData) &&
        graphTotalData.length === graphPeriodCount;

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };
    //console.log(data)
    return (
        <>
            {!!loading ? (
                <Box sx={{ width: '100%', marginTop: '40vh' }}>
                    <LinearProgress />
                </Box>
            ) : (
                <>
                    <FlexRow>
                        <DD2
                            label={'Periodicity'}
                            emptyLabel={'Select a periodicity'}
                            options={periodicities}
                            value={periodicity}
                            onChange={setPeriodicity}
                            displayProperty={'name'}
                            disabled={false}
                        />
                        <FlexRow />
                    </FlexRow>
                    <FlexRow>
                        <DatePickerInput
                            label={'Start Date'}
                            value={startDate}
                            onChange={setStartDate}
                            maxDate={endDate}
                            minDate={new Date('2023-01-01T00:00:00Z')}
                        />
                        <DatePickerInput
                            label={'End Date'}
                            value={endDate}
                            onChange={setEndDate}
                            maxDate={endDateMaxLimit}
                            minDate={startDate}
                        />
                        <FlexRow></FlexRow>
                        <FlexRow></FlexRow>
                    </FlexRow>
                    <div className="mt-8">
                        <PrimaryButton
                            title={'$ Get Ownership-based Costing Data'}
                            onClick={getUnitCostingData}
                        />
                    </div>
                    {!!data ? (
                        <Box sx={{ width: '100%', marginTop: '30px' }}>
                            <Box
                                sx={{ borderBottom: 1, borderColor: 'divider' }}
                            >
                                <Tabs
                                    value={value}
                                    onChange={handleChange}
                                    aria-label="basic tabs example"
                                >
                                    <Tab label="TABLE" {...a11yProps(0)} />
                                    <Tab label="GRAPH" {...a11yProps(1)} />
                                </Tabs>
                            </Box>
                            <TabPanel value={value} index={0}>
                                {finalData.length > 0 ? (
                                    <MongoTable
                                        data={finalData}
                                        mappingData={mongoMappingData ?? []}
                                        groupBy={OWNERSHIP_TABLE_GROUP_BY}
                                        groupByName={
                                            OWNERSHIP_TABLE_GROUP_BY_LABEL
                                        }
                                        periodicity={periodicity?.id}
                                        setTotalData={setTotalData}
                                        toolName={tool.id}
                                    />
                                ) : (
                                    <></>
                                )}
                            </TabPanel>
                            <TabPanel value={value} index={1}>
                                <div className="mt-8 min-w-[1500px] p-4">
                                    {graphChartReady ? (
                                        <>
                                            <FormControlLabel
                                                className="mb-4 block"
                                                control={
                                                    <Checkbox
                                                        checked={
                                                            showGroupBreakdown
                                                        }
                                                        onChange={(e) =>
                                                            setShowGroupBreakdown(
                                                                e.target.checked
                                                            )
                                                        }
                                                        color="primary"
                                                    />
                                                }
                                                label={`Show cost breakdown by ${OWNERSHIP_TABLE_GROUP_BY_LABEL} (stacked columns)`}
                                            />
                                            <OvertimeDescription
                                                data={graphRows}
                                                groupBy={OWNERSHIP_TABLE_GROUP_BY}
                                                groupByName={
                                                    OWNERSHIP_TABLE_GROUP_BY_LABEL
                                                }
                                                periodicity={periodicity?.id}
                                                totalCost={graphTotalData}
                                                alwaysColumnTotal
                                                showServiceBreakdown={
                                                    showGroupBreakdown
                                                }
                                                enableBreakdownForDaily
                                                breakdownExcludeCluster={
                                                    false
                                                }
                                            />
                                        </>
                                    ) : (
                                        <Box sx={{ mt: 2 }}>
                                            Unable to build chart (no period
                                            columns in the response).
                                        </Box>
                                    )}
                                </div>
                            </TabPanel>
                        </Box>
                    ) : (
                        <></>
                    )}
                </>
            )}
        </>
    );
}
